import { useState, useMemo } from "react";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import ObjectHeading from "@/components/ObjectHeading";
import type { Action } from "@/components/MenuButton";
import { renderStatus, renderPaymentStatus, paymentDate } from "../../_utils";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { PaymentType, OrderPayment } from "../[orderId]/payment/[paymentId]/types";
import { toFormat } from "@/lib/dateHelpers";
import { renderPaymentType, getPayerOption } from "../../_utils";
import EmptyState from "@/components/EmptyState";
import Table, {
  TableHeading,
  TableFooter,
  buildActionColumn,
  buildExpandableCountColumn,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildTableFooterLineItems
} from "@/components/Table";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { sumBy, sortBy, capitalize } from "lodash";
import { MaterialNames } from "@/components/material_symbol_names";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import DetailsCard from "@/components/DetailsCard";
import ListItem from "@/components/ListItem/ListItem";
import { useNavigate, type Path } from "@/router";
import { useParams } from "react-router-dom";
import { PaymentStatus, PaymentMethod, OrderStage, OrderPaymentStatus } from "@/pages/billing/types";
import RefundPaymentModal from "./RefundPaymentModal";
import { isUncollectibleOrderPayment } from "./uncollectibleRefund";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Banner from "@/components/Banner";
import SectionHeading from "@/components/SectionHeading";
import type { DocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { COMMERCE_PAYMENT_AUDIT_LOG_QUERY } from "../payment/[paymentId]/queries";
import { useStepsList } from "../../_utils";
import { CommercePaymentAuditLogQuery } from "../[orderId]/types";
import HistorySection from "./HistorySection";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import NotesTable, { useNoteDeepLink, usePinnedNotes } from "@/features/global/components/NotesTable";
import TasksTable from "@/features/global/components/TasksTable";
import AddTaskSideSheet from "@/features/global/components/AddTaskSideSheet/AddTaskSideSheet";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import Breadcrumb from "@/components/Breadcrumb";
import { useReturnTo } from "@/hooks/useSearchParams";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { NoteNotableValues } from "@/types";

interface OrderTableRow {
  id: string;
  orderPaymentId: string;
  person: {
    profilePhoto?: string;
    firstName: string;
    lastName: string;
    pgaId?: string;
    mainProgramType?: {
      name: string;
    } | null;
  };
  description: string;
  dueDate: string;
  pgaId?: string;
  balance: string;
  balanceCents: number;
  totalPaid: string;
  totalPaidCents: number;
  status: OrderStage;
  paymentStatus: OrderPaymentStatus;
  eventRegistration: {
    id: string;
    event: {
      title: string;
      slug: string;
      startsAt: string;
      activityType: string;
    };
  } | null;
  lineItems: Array<{
    id: string;
    name: string;
    payment: string;
    type: string;
    amountCents: number;
    subLineItem?: boolean;
    indent?: boolean;
  }>;
}

export const PaymentWrapper = ({
  payment,
  refetch,
  paymentQuery
}: {
  payment: PaymentType;
  refetch: () => void;
  paymentQuery: DocumentNode;
}) => {
  const { hasPermission } = useCapabilities();
  const {
    highlightNoteId: deepLinkNoteId,
    openNoteId,
    getCopyLink,
    setOpenNoteInUrl
  } = useNoteDeepLink({ dataReady: true });
  const canManageTasks = useCanManageTasksBool();
  const showTasks = canManageTasks;
  const currentUser = useCurrentBackofficeUser();
  const canAddNotes = hasPermission(PERMISSION.managePayments);
  const canAddTasks = showTasks;

  const {
    pinnedNotes,
    shouldAutoOpenNotes,
    highlightNoteId: pinnedHighlightNoteId
  } = usePinnedNotes({
    notes: payment.notes || [],
    entityId: payment.id,
    sessionStorageKey: "visitedPaymentDetails"
  });
  const highlightNoteId = pinnedHighlightNoteId ?? deepLinkNoteId;

  const { Menu, setMenu } = useTableRowMenu();
  const [openRefundPaymentModal, setOpenRefundPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderPayment | null>(null);
  const [openRefundWithCheckModal, setOpenRefundWithCheckModal] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  const { data: auditLogData, loading: auditLogDataLoading } = useQuery<CommercePaymentAuditLogQuery>(
    COMMERCE_PAYMENT_AUDIT_LOG_QUERY,
    {
      variables: { paymentId: payment.id },
      errorPolicy: "all"
    }
  );

  const auditLogEntries = auditLogData?.commercePaymentAuditLog || [];

  const {
    paidAt,
    amountCents,
    refundedAmountCents,
    type,
    transactionId,
    transactionUrl,
    stripeAccount,
    orderPayments,
    payer,
    status
  } = payment;

  const orderDataArray = orderPayments?.map((orderPayment: OrderPayment) => {
    const { order } = orderPayment;
    return {
      ...order,
      id: order.id,
      orderPaymentId: orderPayment.id,
      dueDate: toFormat(order?.dueDate) || "",
      pgaId: order?.person?.pgaId,
      balance: centsToDollarsFormatted(Number(order?.balance)),
      balanceCents: order?.balance,
      totalPaid: centsToDollarsFormatted(sumBy(orderPayment.ledgerEntries, "amountCents")),
      paidAmountCents: centsToDollarsFormatted(
        sumBy(
          orderPayment.ledgerEntries.filter((entry) => entry.type === "paid"),
          "amountCents"
        )
      ),
      refundedAmountCents: centsToDollarsFormatted(
        sumBy(
          orderPayment.ledgerEntries.filter((entry) => entry.type === "refunded"),
          "amountCents"
        )
      ),
      totalPaidCents: sumBy(orderPayment.ledgerEntries, "amountCents"),
      ledgerEntries: orderPayment.ledgerEntries,
      lineItemsOriginal: order.lineItems,
      person: order.person,
      lineItems:
        orderPayment.ledgerEntries?.map((ledgerEntry) => {
          const { secondaryLineItem } = ledgerEntry;
          return [
            {
              ...ledgerEntry.lineItem,
              name:
                ledgerEntry.type === "refunded"
                  ? `${
                      ledgerEntry.amountCents === -ledgerEntry.lineItem.priceCents ? "Full" : "Partial"
                    } Refund - ${toFormat(ledgerEntry.createdAt)}`
                  : ledgerEntry.type === "transferred_to"
                    ? `Payment Transfer to ${secondaryLineItem.name} - ${toFormat(ledgerEntry.createdAt)}`
                    : ledgerEntry.lineItem.name,
              payment: centsToDollarsFormatted(ledgerEntry.amountCents),
              type: ledgerEntry.type,
              amountCents: ledgerEntry.amountCents,
              id: `${ledgerEntry.lineItem.id}-${Date.now()}`,
              proration: ledgerEntry.lineItem.proration,
              subLineItem: ledgerEntry.type === "refunded" || ledgerEntry.type === "transferred_to",
              indent: ledgerEntry.type === "refunded" || ledgerEntry.type === "transferred_to"
            }
          ];
        }) || [],
      lineItemsTotal: sumBy(orderPayment.ledgerEntries, "amountCents"),
      eventRegistration: order.eventRegistration
    };
  });

  const orderData = orderDataArray.map((order) => {
    const { lineItems } = order;
    return {
      ...order,
      lineItems: sortBy(lineItems.flat(), (item) => [item.id, -item.amountCents])
    };
  });

  const payerOption = getPayerOption(payer.type);
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId?: string }>();
  const returnTo = useReturnTo();
  const stepsList = useStepsList(auditLogEntries, 0);
  const { isCompactLayout } = useIsCompactLayout();

  function closeRefundModal() {
    setOpenRefundPaymentModal(false);
  }

  const copyCopNumber = async (copNumber: string) => {
    if (!navigator?.clipboard?.writeText) {
      showErrorSnackbar("Failed to copy COP number to clipboard");
      return;
    }

    try {
      await navigator.clipboard.writeText(copNumber);
      showSnackbar("COP number copied to clipboard", { close: true });
    } catch {
      showErrorSnackbar("Failed to copy COP number to clipboard");
    }
  };

  const headerActions = useMemo<Action[]>(() => {
    const buttons: Action[] = [];
    const registrationOrderPayment = orderPayments?.find((op) => op.order.eventRegistration);
    const registration = registrationOrderPayment?.order.eventRegistration;
    const registrationOrderId = registrationOrderPayment?.order.id;
    const hasRegistration = !!(registration && registrationOrderId);
    const hasPATRegistration = orderPayments?.some((op) => op.order.eventRegistration?.event?.activityType === "PAT");
    const hasUncollectibleRelatedOrder = orderPayments?.some(isUncollectibleOrderPayment);

    if (hasPermission(PERMISSION.managePayments) && status === PaymentStatus.PAID && refundError) {
      buttons.push({
        text: "Refund With Check",
        variant: "text",
        onClick: () => setOpenRefundWithCheckModal(true)
      });
    }

    if (
      hasPermission(PERMISSION.managePayments) &&
      status === PaymentStatus.PAID &&
      (!hasRegistration || hasPATRegistration) &&
      !hasUncollectibleRelatedOrder
    ) {
      buttons.push({
        text: "Refund Full Payment",
        variant: "outlined",
        onClick: () => {
          setOpenRefundPaymentModal(true);
          setSelectedOrder(null);
        }
      });
    }

    if (hasRegistration) {
      buttons.push({
        text: "View Registration",
        testId: "payment-view-registration",
        onClick: () => {
          navigate(
            `/activities/events/${registration.event.slug}/registrations/${registration.id}?return_to=/billing/orders/${registrationOrderId}/payment/${payment.id}` as Path
          );
        }
      });
    }
    if (canAddNotes) {
      buttons.push({
        text: "Add Note",
        variant: "outlined",
        onClick: () => setAddNoteOpen(true),
        testId: "add-note-action",
        section: "Actions"
      });
    }

    if (canAddTasks) {
      buttons.push({
        text: "Add Task",
        variant: "outlined",
        onClick: () => setAddTaskOpen(true),
        testId: "add-task-action",
        section: "Actions"
      });
    }

    return buttons;
  }, [hasPermission, status, refundError, canAddNotes, canAddTasks, orderPayments, navigate, payment.id]);

  return (
    <>
      <div className="-mx-12">{refundError && <Banner description={refundError} closable variant="error" />}</div>
      <Menu />
      <div>
        <Breadcrumb
          title="Payments"
          backLink={{ to: "/billing/payments" }}
          compactTitle="Payment Details"
          onBackClick={() => {
            const backTo = returnTo || (orderId ? `/billing/orders/${orderId}` : "/billing/payments");
            navigate(backTo as Path);
          }}
        />
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <ObjectHeading
            title="Payment"
            iconName="attach_money"
            isDetailsView
            tag={<div>{renderPaymentStatus(payment.status, "strong")}</div>}
            metaData={[
              ...(paidAt ? [{ text: paymentDate(type, paidAt) || "", label: "Date" }] : []),
              ...(amountCents
                ? [
                    {
                      text: centsToDollarsFormatted(amountCents) || "",
                      label: "Amount"
                    }
                  ]
                : []),
              ...(status === PaymentStatus.REFUNDED || status === PaymentStatus.PARTIALLY_REFUNDED
                ? [
                    {
                      text: centsToDollarsFormatted(refundedAmountCents) || "",
                      label: "Refund"
                    }
                  ]
                : []),
              ...(status === PaymentStatus.REFUNDED || status === PaymentStatus.PARTIALLY_REFUNDED
                ? [
                    {
                      text: centsToDollarsFormatted(amountCents - refundedAmountCents) || "",
                      label: "Net"
                    }
                  ]
                : [])
            ]}
            actions={headerActions}
            pinnedNotes={pinnedNotes}
            initialNotesDialogOpen={shouldAutoOpenNotes}
          />
        </div>
        {type && (
          <>
            <SectionHeading heading="Payment Method" />
            <ListItem
              iconName={type === PaymentMethod.CHECK ? "account_balance" : "credit_card"}
              iconBackground
              compact
              headline={`${type === PaymentMethod.FINANCIAL_ADJUSTMENT ? payment.payer.name : renderPaymentType(type)}${
                !!payment.referenceNumber ? ` #${payment.referenceNumber}` : ""
              }`}
            />
          </>
        )}
        <HistorySection stepsList={stepsList} loading={auditLogDataLoading} />
        {transactionId && (
          <>
            <SectionHeading heading="Transaction Details" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <DetailsCard
                heading={
                  type === PaymentMethod.FINANCIAL_ADJUSTMENT
                    ? payment.payer.name
                    : `Collection via ${type === PaymentMethod.CHECK ? " Check" : "Stripe"}`
                }
                options={[
                  {
                    iconName: "receipt_long",
                    overline: "Transaction ID",
                    headline: transactionId,
                    ...(type === PaymentMethod.CARD || type === PaymentMethod.US_BANK_ACCOUNT
                      ? {
                          action: () => window.open(transactionUrl, "_blank"),
                          actionIcon: "open_in_new",
                          supportingText: `Account: ${capitalize(stripeAccount)}`
                        }
                      : {})
                  }
                ]}
              />
            </div>
          </>
        )}
        {type !== PaymentMethod.FINANCIAL_ADJUSTMENT && (
          <>
            <SectionHeading heading="Summary" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <DetailsCard
                heading="Paid By"
                options={[
                  {
                    iconName: payerOption.icon as MaterialNames,
                    overline: payerOption.label,
                    headline: payer.name
                  }
                ]}
              />

              {payer.email && (
                <DetailsCard
                  heading="Email Address"
                  options={[
                    {
                      iconName: "mail",
                      overline: "",
                      headline: payer.email
                    }
                  ]}
                />
              )}
            </div>
          </>
        )}
        <TableHeading heading="Orders Included in Payment" />
        <Table<OrderTableRow>
          data={(orderData || []) as OrderTableRow[]}
          loading={false}
          onClickRow={(row) =>
            navigate(`/billing/orders/:orderId`, {
              params: { orderId: row.original.id }
            })
          }
          renderExpandedRow={({ row }) => (
            <TableDetailsPanel
              data={row.original.lineItems}
              title={`Products (${row.original.lineItems.length})`}
              columns={[
                {
                  accessorKey: "name",
                  header: "Product Name",
                  enableSorting: false,
                  size: 1200,
                  meta: { headerClassName: "hide-header-in-column" },
                  cell: (cellProps) => {
                    return <span>{cellProps.row.original.name}</span>;
                  }
                },
                {
                  accessorKey: "payment",
                  header: "Payment",
                  enableSorting: false,
                  size: 240,
                  meta: {
                    className: "text-right",
                    headerClassName: "justify-end"
                  },
                  cell: (cellProps) => {
                    return <span>{cellProps.row.original.payment}</span>;
                  }
                }
              ]}
              renderFooter={() => (
                <>
                  {buildTableFooterLineItems({
                    items: [
                      {
                        label: "Total",
                        value: row.original.totalPaidCents,
                        isEmphasized: true,
                        className: "max-lg:pr-4"
                      }
                    ],
                    isSingle: true
                  })}
                </>
              )}
            />
          )}
          columns={[
            ...buildAvatarDetailsColumn<OrderTableRow>({
              isCompactLayout,
              getTitle: (row) => `${row.person.firstName} ${row.person.lastName}`,
              getSubtitle: (row) => row.person.mainProgramType?.name || "",
              getPicture: (row) => row.person.profilePhoto,
              header: "Name",
              size: 200,
              id: "fullName"
            }).map((col) => ({
              ...col,
              meta: {
                ...col.meta,
                className: getStickyColumnClasses(!!orderData?.length)
              }
            })),
            buildCopyTextColumn<OrderTableRow>({
              accessorKey: "pgaId",
              header: "PGA ID",
              copyFieldLabel: "PGA ID",
              getText: (row) => row.pgaId || "",
              enableSorting: false
            }),
            {
              accessorKey: "description",
              header: "Description",
              enableSorting: false
            },
            buildExpandableCountColumn({
              accessorKey: "products",
              header: "Products",
              getCount: (row) => row.lineItems.length
            }),
            {
              accessorKey: "paidAmountCents",
              header: "Paid",
              enableSorting: false
            },
            {
              accessorKey: "refundedAmountCents",
              header: "Refunded",
              enableSorting: false
            },
            {
              accessorKey: "totalPaid",
              header: "Net Payment",
              enableSorting: false
            },
            {
              accessorKey: "balance",
              header: "Balance Due",
              enableSorting: false
            },
            {
              header: "Status",
              id: "stage",
              cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
              size: 100,
              meta: {
                headerClassName: "hide-header-in-column"
              }
            },
            ...(hasPermission(PERMISSION.managePayments)
              ? [
                  buildActionColumn<OrderTableRow>({
                    getActions: (row) => {
                      const actions: Action[] = [
                        {
                          text: "Copy COP Number",
                          copyFieldLabel: "COP number",
                          copyFieldId: row.orderPaymentId,
                          onClick() {
                            void copyCopNumber(row.orderPaymentId);
                          }
                        }
                      ];

                      if (
                        row.eventRegistration
                          ? row.eventRegistration.event?.activityType === "PAT"
                          : row.totalPaidCents > 0
                      ) {
                        actions.push({
                          text: "Refund Partial Order Payment",
                          onClick() {
                            setOpenRefundPaymentModal(true);
                            setSelectedOrder(orderData.find((order) => order.id === row.id) as unknown as OrderPayment);
                          }
                        });
                      }

                      return actions;
                    },
                    actionId,
                    setMenu
                  })
                ]
              : [])
          ]}
          renderNoDataEmptyState={() => (
            <EmptyState title="No Orders Found" caption="No orders found for this payment." iconName="box" />
          )}
          renderFooter={() => <TableFooter totalRowCount={orderData.length} />}
        />
        <NotesTable
          data={payment.notes || []}
          hideControls
          tableHeading="Notes"
          tableBody="View and manage notes for this payment"
          highlightNoteId={highlightNoteId}
          openNoteId={openNoteId}
          onOpenNoteChange={setOpenNoteInUrl}
          getCopyLink={getCopyLink}
          noteEditing={{
            enabled: hasPermission(PERMISSION.managePayments),
            notableId: payment.id,
            notableType: NoteNotableValues.PAYMENT,
            refetchQueries: [paymentQuery],
            ...(orderPayments?.length === 1 && orderPayments[0].order.person?.pgaId
              ? { pgaId: orderPayments[0].order.person.pgaId }
              : {})
          }}
        />
        {showTasks && (
          <TasksTable
            referenceableId={payment.id}
            referenceableType="PAYMENT"
            tableHeading="Tasks"
            tableBody="View and manage tasks for this payment"
            hideControls
          />
        )}
        {openRefundPaymentModal && (
          <RefundPaymentModal
            payment={payment}
            onDismiss={closeRefundModal}
            refetch={refetch}
            setErrorMessage={setRefundError}
            order={selectedOrder}
            setSelectedOrder={setSelectedOrder}
          />
        )}
        {openRefundWithCheckModal && (
          <RefundPaymentModal
            payment={{ ...payment, bypass: true }}
            onDismiss={() => {
              setOpenRefundWithCheckModal(false);
            }}
            refetch={refetch}
            setErrorMessage={setRefundError}
            order={selectedOrder}
            setSelectedOrder={setSelectedOrder}
          />
        )}
      </div>
      {canAddNotes && (
        <AddEditNoteSideSheet
          open={addNoteOpen}
          onClose={() => setAddNoteOpen(false)}
          notableId={payment.id}
          notableType={NoteNotableValues.PAYMENT}
          refetchQueries={[paymentQuery]}
          currentUserId={currentUser?.id}
          {...(orderPayments?.length === 1 && orderPayments[0].order.person?.pgaId
            ? { pgaId: orderPayments[0].order.person.pgaId }
            : {})}
        />
      )}
      {canAddTasks && (
        <AddTaskSideSheet
          open={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          currentUser={currentUser}
          defaultReferences={[{ referenceableId: payment.id, referenceableType: "PAYMENT" }]}
        />
      )}
    </>
  );
};

export default PaymentWrapper;
